import { publish, subscribe } from './pubsub';
import { PUB_SUB_EVENTS } from './constants';
import CartPerformance from './cart-performance';
import fetchConfig from './fetch-config';
import CartNotification from './cart-notification';
import CartDrawer from './cart-drawer';

interface CartResponse {
  status?: boolean;
  errors?: string;
  description?: string;
  message?: string;
  [key: string]: unknown;
}

interface VariantQuantityRule {
  min?: number;
  max?: number | null;
  increment?: number;
}

interface ProductVariant {
  id?: number | string;
  available?: boolean;
  inventory_quantity?: number;
  inventory_management?: string | null;
  inventory_policy?: string;
  quantity_rule?: VariantQuantityRule;
}

interface VariantChangeEvent {
  data: {
    sectionId?: string;
    html?: Document;
    variant?: ProductVariant | null;
  };
}

type QuantityPicker = HTMLElement & { validateQtyRules?: () => void };

export default class ProductForm extends HTMLElement {
  private form: HTMLFormElement | null;
  private cartDrawer: CartDrawer | null;
  private cartNotification: CartNotification | null;
  private submitButton: HTMLButtonElement | null;
  private submitButtonText: HTMLElement | null;
  private hideErrors: boolean;
  private error = false;
  private errorMessageWrapper: HTMLElement | null = null;
  private errorMessage: HTMLElement | null = null;
  private quantityInput: HTMLInputElement | null = null;
  private syncingQuantity = false;
  private variantChangeUnsubscriber: (() => void) | undefined;
  private boundQuantityChange = this.onQuantityChange.bind(this);
  private boundVariantChange = this.onVariantChange.bind(this);

  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cartDrawer = document.querySelector('cart-drawer') as CartDrawer;
    this.cartNotification = document.querySelector('cart-notification') as CartNotification;

    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');

    if (document.querySelector('cart-drawer') && !this.submitButton.hasAttribute('data-drawer-upsell')) {
      this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    this.hideErrors = this.dataset.hideErrors === 'true';
    this.quantityInput = this.form?.querySelector('input[name="quantity"]') ?? null;
  }

  connectedCallback() {
    this.quantityInput?.addEventListener('change', this.boundQuantityChange);
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.boundVariantChange);
  }

  disconnectedCallback() {
    this.quantityInput?.removeEventListener('change', this.boundQuantityChange);
    this.variantChangeUnsubscriber?.();
  }

  onSubmitHandler(evt) {
    evt.preventDefault();
    if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

    this.handleErrorMessage();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.setAttribute('disabled', 'disabled');
    this.querySelector('#loading-spinner')?.classList.remove('hidden');

    const extendedWarrantyInput = document.querySelector('#ExtendedWarranty') as HTMLInputElement | null;
    const shouldAddExtendedWarranty =
      !!extendedWarrantyInput && extendedWarrantyInput.checked && extendedWarrantyInput.dataset.variantId;

    const ssdCheckedInput = document.querySelector('input[name="ssd-storage-size"]:checked') as HTMLInputElement | null;
    const shouldAddSsdUpsell = !!ssdCheckedInput && !!ssdCheckedInput.value && !!ssdCheckedInput.dataset.variantId;

    const wlanInput = document.querySelector('#upsell-wlan') as HTMLInputElement | null;
    const shouldAddWlan = !!wlanInput && wlanInput.checked && !!wlanInput.dataset.variantId;

    const bluetoothInput = document.querySelector('#upsell-bluetooth') as HTMLInputElement | null;
    const shouldAddBluetooth = !!bluetoothInput && bluetoothInput.checked && !!bluetoothInput.dataset.variantId;

    const linuxInput = document.querySelector('#upsell-linux') as HTMLInputElement | null;
    const shouldAddLinux = !!linuxInput && linuxInput.checked && !!linuxInput.dataset.variantId;

    this.addToCart(
      evt,
      shouldAddExtendedWarranty ? extendedWarrantyInput : null,
      shouldAddSsdUpsell ? ssdCheckedInput : null,
      shouldAddWlan ? wlanInput : null,
      shouldAddBluetooth ? bluetoothInput : null,
      shouldAddLinux ? linuxInput : null,
    );
  }

  private addToCart(
    evt: Event,
    extendedWarrantyInput: HTMLInputElement | null = null,
    ssdUpsellInput: HTMLInputElement | null = null,
    wlanInput: HTMLInputElement | null = null,
    bluetoothInput: HTMLInputElement | null = null,
    linuxInput: HTMLInputElement | null = null,
  ) {
    const properties = this.collectLineItemProperties();
    const items = [
      {
        id: this.variantIdInput.value,
        quantity: this.getSelectedQuantity(),
        ...(properties ? { properties } : {}),
      },
    ];

    const prodId = this.getAttribute('data-product-id') || '';
    const linkedTitle =
      this.dataset.productTitle || document.querySelector('product-form')?.getAttribute('data-product-title') || '';

    if (extendedWarrantyInput?.dataset.variantId) {
      items.push({
        id: extendedWarrantyInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (ssdUpsellInput?.dataset.variantId) {
      items.push({
        id: ssdUpsellInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (wlanInput?.dataset.variantId) {
      items.push({
        id: wlanInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (bluetoothInput?.dataset.variantId) {
      items.push({
        id: bluetoothInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    if (linuxInput?.dataset.variantId) {
      items.push({
        id: linuxInput.dataset.variantId,
        quantity: 1,
        properties: { 'Linked to': linkedTitle, '_Product ID': prodId },
      });
    }

    let formData = { items };
    if (this.cartDrawer || this.cartNotification) {
      formData = { ...formData, ...this.getCartSectionsData() };
    }

    const config = { ...fetchConfig(), body: JSON.stringify(formData) };
    this.fetchAndHandleCartResponse(`${window.routes.cart_add_url}`, config, this.variantIdInput.value, evt);
  }

  private collectLineItemProperties(): Record<string, string> | undefined {
    const formEl = this.form as HTMLFormElement | null;
    if (!formEl) return undefined;

    const formId = formEl.getAttribute('id') || '';
    const insideFormElements = Array.from(
      formEl.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('[name^="properties["]'),
    );
    const associatedElements = formId
      ? Array.from(
          document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
            `[form="${CSS.escape(formId)}"][name^="properties["]`,
          ),
        )
      : [];
    const elements = [...insideFormElements, ...associatedElements];

    const properties: Record<string, string> = {};
    elements.forEach((el) => {
      if (el.disabled) return;

      if (el instanceof HTMLInputElement && (el.type === 'radio' || el.type === 'checkbox')) {
        if (!el.checked) return;
      }

      const match = el.name.match(/^properties\[(.+)\]$/);
      if (!match) return;
      const key = match[1];
      const value = (el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).value;
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        properties[key] = value;
      }
    });

    return Object.keys(properties).length ? properties : undefined;
  }

  private getCartSectionsData() {
    const sections = [];

    if (this.cartDrawer) {
      sections.push(...this.cartDrawer.getSectionsToRender().map((section) => section.id));
    }

    if (this.cartNotification) {
      sections.push(...this.cartNotification.getSectionsToRender().map((section) => section.section));
      this.cartNotification.setActiveElement(document.activeElement);
    }

    const data = {
      sections: [...new Set(sections)],
      sections_url: window.location.pathname,
    };

    const bundleInfo = this.getBundleInfo();
    if (bundleInfo) {
      data['properties[_Bundle info]'] = bundleInfo;
    }

    return data;
  }

  private getBundleInfo() {
    const bundleItems = document.querySelectorAll('[id*="bundle-item-"]');
    if (bundleItems.length === 0) return null;

    return Array.from(bundleItems)
      .map((bundle) => bundle.innerHTML?.trim())
      .filter(Boolean)
      .join(', ');
  }

  private fetchAndHandleCartResponse(url: string, config: RequestInit, productVariantId: string | number, evt: Event) {
    fetch(url, config)
      .then((response) => response.json())
      .then((response) => this.handleCartResponse(response, productVariantId, evt))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      })
      .finally(() => this.resetSubmitButton(evt));
  }

  private handleCartResponse(response: CartResponse, productVariantId: string | number, _evt: Event) {
    if (response.status || response.errors) {
      this.handleCartError(response, productVariantId);
    } else if (!this.cartDrawer) {
      window.location = window.routes.cart_url;
    } else {
      this.handleCartSuccess(response, productVariantId);
    }
  }

  private handleCartError(response: CartResponse, productVariantId: string | number) {
    const errorMessage = response.errors || response.description;

    publish(PUB_SUB_EVENTS.cartError, {
      source: 'product-form',
      productVariantId,
      errors: errorMessage,
      message: response.message,
    });

    this.handleErrorMessage(errorMessage);

    const soldOutMessage = this.submitButton.querySelector('#sold-out-message');
    if (soldOutMessage) {
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButtonText.classList.add('hidden');
      soldOutMessage.classList.remove('hidden');
      this.error = true;
    }
  }

  private handleCartSuccess(response: CartResponse, productVariantId: string | number) {
    const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
    if (!this.error) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId,
        cartData: response,
      }).then(() => {
        CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
      });
    }
    this.error = false;

    const quickAddModal = this.closest('quick-add-modal');
    if (quickAddModal) {
      this.handleQuickAddModal(quickAddModal, response);
    } else {
      CartPerformance.measure('add:paint-updated-sections', () => {
        this.cartNotification.renderContents(response);
      });
    }

    this.resetQuantityPicker();
  }

  private resetQuantityPicker() {
    if (!this.quantityInput) return;

    const min = parseInt(this.quantityInput.min || this.quantityInput.dataset.min || '1', 10);
    const resetValue = Number.isFinite(min) && min > 1 ? min : 1;
    this.quantityInput.value = String(resetValue);
    this.syncQuantityInputs(String(resetValue));

    const picker = this.quantityInput.closest('custom-quantity-input') as QuantityPicker | null;
    picker?.validateQtyRules?.();
  }

  private handleQuickAddModal(quickAddModal: Element, response: CartResponse) {
    document.body.addEventListener(
      'modalClosed',
      () => {
        setTimeout(() => {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cartNotification.renderContents(response);
          });
        });
      },
      { once: true },
    );
    quickAddModal.hide(true);
  }

  private resetSubmitButton(evt: Event) {
    this.submitButton.removeAttribute('disabled');

    if (this.cartDrawer?.classList.contains('is-empty')) {
      this.cartDrawer.classList.remove('is-empty');
    }

    if (!this.error) {
      this.submitButton.removeAttribute('aria-disabled');
    }

    this.querySelector('#loading-spinner')?.classList.add('hidden');
    CartPerformance.measureFromEvent('add:user-action', evt);
  }

  handleErrorMessage(errorMessage?: string | false) {
    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('#error-message-wrapper');

    if (!this.errorMessageWrapper) return;

    this.errorMessageWrapper.classList.add('hidden');

    if (this.hideErrors || !errorMessage) return;

    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('#error-message');
    if (this.errorMessage) {
      this.errorMessage.textContent = errorMessage;
      this.errorMessageWrapper.classList.add('flex');
      this.errorMessageWrapper.classList.remove('hidden');
    }
  }

  toggleSubmitButton(disable: boolean = true, text?: string): void {
    if (!this.submitButton || !this.submitButtonText) return;

    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) {
        this.submitButtonText.textContent = text;
      }
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent =
        (window as Window & { variantStrings?: { addToCart: string } }).variantStrings?.addToCart || 'Add to cart';
    }
  }

  private getSelectedQuantity(): number {
    const value = parseInt(this.quantityInput?.value || '', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  private onQuantityChange() {
    if (this.syncingQuantity || !this.quantityInput) return;
    this.syncQuantityInputs(this.quantityInput.value);
  }

  private onVariantChange(event: VariantChangeEvent) {
    if (!this.quantityInput) return;

    const sectionId = event?.data?.sectionId;
    if (sectionId && this.dataset.sectionId && sectionId !== this.dataset.sectionId) return;

    const variant = event?.data?.variant;
    if (!variant) {
      this.setQuantityPickerDisabled(true);
      return;
    }

    const html = event?.data?.html;
    const sourceInput =
      (sectionId ? html?.getElementById(`Quantity-${sectionId}`) : null) ??
      html?.querySelector<HTMLInputElement>('product-form input[name="quantity"]') ??
      null;

    if (!(sourceInput instanceof HTMLInputElement)) return;

    // Clear max before writing value so the number input does not keep a stale quantity.
    this.quantityInput.removeAttribute('max');
    delete this.quantityInput.dataset.max;

    this.quantityInput.min = sourceInput.min;
    this.quantityInput.dataset.min = sourceInput.dataset.min ?? sourceInput.min;
    this.quantityInput.step = sourceInput.step;
    this.quantityInput.dataset.cartQuantity = sourceInput.dataset.cartQuantity ?? '0';
    this.quantityInput.value = sourceInput.value;

    if (sourceInput.hasAttribute('max')) {
      this.quantityInput.max = sourceInput.max;
      this.quantityInput.dataset.max = sourceInput.dataset.max ?? sourceInput.max;
    }

    this.setQuantityPickerDisabled(sourceInput.disabled);

    const picker = this.quantityInput.closest('custom-quantity-input') as QuantityPicker | null;
    picker?.validateQtyRules?.();
    this.updateQuoteButtons(this.quantityInput.value);
    publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  private syncQuantityInputs(value: string) {
    this.syncingQuantity = true;

    document.querySelectorAll<HTMLInputElement>('product-form input[name="quantity"]').forEach((input) => {
      if (input === this.quantityInput) return;
      input.value = value;
      const picker = input.closest('custom-quantity-input') as QuantityPicker | null;
      picker?.validateQtyRules?.();
    });

    this.syncingQuantity = false;
    this.updateQuoteButtons(value);
  }

  private updateQuoteButtons(value: string) {
    const qty = parseInt(value, 10) || 0;
    document.querySelectorAll<HTMLElement>('[data-rfq-min-qty]').forEach((button) => {
      const minQty = parseInt(button.dataset.rfqMinQty || '0', 10);
      button.classList.toggle('!hidden', qty < minQty);
    });
  }

  private setQuantityPickerDisabled(disabled: boolean) {
    const picker = this.quantityInput?.closest('custom-quantity-input');
    if (!picker || !this.quantityInput) return;

    picker.classList.toggle('opacity-50', disabled);
    picker.classList.toggle('pointer-events-none', disabled);
    this.quantityInput.disabled = disabled;
    picker.querySelectorAll<HTMLButtonElement>('button').forEach((button) => {
      button.disabled = disabled;
    });
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
